import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { fetchPromoCodes, deletePromoCode } from '../../services/promocode-service.js';
import { useLanguage } from '../../utils/languageContext.js';
import { getTranslation } from '../../utils/translate.js';
import CustomSelect from '../comman/select.js';
import RowsPerPageSelect from '../comman/RowsPerPageSelect.js';
import Pagination from '../comman/pagination.js';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants/pagination.js';
import ConfirmationModal from '../Modal/ConfirmationModal.jsx';
import LoadingSpinner from '../LoadingSpinner/loadingSpinner.js';
import ErrorDisplay from '../comman/ErrorDisplay.js';
import Button from '../comman/button.js';
import Dropdown from '../comman/dropdown.js';
import DropdownMenuItem from '../comman/dropdownMenuItem.js';
import ellipsis from '../../assets/ellipsis.svg';

const PromocodesTable = ({ onEditPromocode, onCreatePromocode, toastRef }) => {
    const { language } = useLanguage();
    const [promocodes, setPromocodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

    // Filters
    const [typeFilter, setTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const hasFiltered = useRef(false);
    const isFetching = useRef(false);

    // Dropdown states
    const [openDropdown, setOpenDropdown] = useState(null);

    // Delete modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [promocodeToDelete, setPromocodeToDelete] = useState(null);

    // Memoize filter options to prevent recreation on every render
    const typeOptions = useMemo(() => [
        { value: '', label: getTranslation('promocodes.type', language) },
        { value: 'FIXED', label: getTranslation('promocodes.types.FIXED', language) },
        { value: 'PERCENTAGE', label: getTranslation('promocodes.types.PERCENTAGE', language) },
        { value: 'DELIVERY_PERCENTAGE', label: getTranslation('promocodes.types.DELIVERY_PERCENTAGE', language) }
    ], [language]);

    const statusOptions = useMemo(() => [
        { value: '', label: getTranslation('promocodes.status', language) },
        { value: 'true', label: getTranslation('common.active', language) },
        { value: 'false', label: getTranslation('common.inactive', language) }
    ], [language]);

    const fetchData = useCallback(async () => {
        // Prevent concurrent fetches
        if (isFetching.current) return;
        
        isFetching.current = true;
        setError(null);
        try {
            setLoading(true);
            const params = {
                offset: (currentPage - 1) * itemsPerPage,
                limit: itemsPerPage,
                // Only include filters if user actually set them (not on initial load)
                ...(hasFiltered.current && typeFilter && { type: typeFilter }),
                ...(hasFiltered.current && statusFilter !== '' && { isActive: statusFilter === 'true' })
            };

            const response = await fetchPromoCodes(params);
            // Filter out PLATFORM and SPLIT promocodes - chefs should only see CHEF promocodes
            const filteredPromocodes = (response.promoCodeDtoList || []).filter(
                promocode => promocode.sponsor !== 'PLATFORM' && promocode.sponsor !== 'SPLIT'
            );
            setPromocodes(filteredPromocodes);
            // Adjust count to reflect filtered results
            setTotalCount(filteredPromocodes.length);
            setError(null); // Clear any previous errors
        } catch (error) {
            console.error("Error fetching promocodes:", error);
            setError(error);
            // Clear data on error
            setPromocodes([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
            isFetching.current = false;
        }
    }, [currentPage, itemsPerPage, typeFilter, statusFilter]);

    // Fetch on initial mount and when dependencies change
    useEffect(() => {
        fetchData();
    }, [fetchData]);


    const handleDeleteClick = (promocode) => {
        setPromocodeToDelete(promocode);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (promocodeToDelete) {
            try {
                await deletePromoCode(promocodeToDelete.id);
                toastRef.current?.addToast(
                    getTranslation('promocodes.deletedSuccess', language) || "Promocode deleted successfully",
                    "success"
                );
                fetchData();
                setShowDeleteModal(false);
                setPromocodeToDelete(null);
            } catch (error) {
                console.error('Error deleting promocode:', error);
                toastRef.current?.addToast(
                    getTranslation('common.error', language) || "An error occurred",
                    "error"
                );
            }
        }
    };

    const handleTypeFilterChange = (selected) => {
        const newValue = selected?.value || '';
        if (newValue !== typeFilter) {
            hasFiltered.current = true;
        }
        setTypeFilter(newValue);
        setCurrentPage(1);
    };

    const handleStatusFilterChange = (selected) => {
        const newValue = selected?.value || '';
        if (newValue !== statusFilter) {
            hasFiltered.current = true;
        }
        setStatusFilter(newValue);
        setCurrentPage(1);
    };

    // Memoize helper functions to prevent recreation
    const formatDate = useCallback((dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('am-AM');
    }, []);

    const getStatusDisplay = useCallback((isActive) => {
        return isActive 
            ? getTranslation('common.active', language)
            : getTranslation('common.inactive', language);
    }, [language]);

    const getStatusColor = useCallback((isActive) => {
        return isActive ? 'text-green-600' : 'text-red-600';
    }, []);

    const getTypeDisplay = useCallback((type) => {
        return getTranslation(`promocodes.types.${type}`, language) || type;
    }, [language]);


    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return (
            <ErrorDisplay
                error={error}
                onRetry={fetchData}
            />
        );
    }

    return (
        <div className="bg-white dark:bg-[#161b22] rounded-lg shadow border dark:border-[#30363d] mb-8 overflow-visible">
            {/* Header with filters */}
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-[#30363d]">
                <div className="flex flex-col gap-4">
                    {/* Filters Row */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        <div className="text-sm text-gray-600 dark:text-[#b1bac4] font-medium whitespace-nowrap">
                            Ֆիլտրել ըստ:
                        </div>
                        <div className="flex flex-col sm:flex-row gap-4 flex-1 min-w-0">
                            <div className="w-full sm:flex-1 sm:min-w-0">
                                <CustomSelect
                                    options={typeOptions}
                                    value={typeFilter}
                                    onChange={handleTypeFilterChange}
                                    placeholder={getTranslation('promocodes.type', language)}
                                    margin="mb-0"
                                />
                            </div>
                            <div className="w-full sm:flex-1 sm:min-w-0">
                                <CustomSelect
                                    options={statusOptions}
                                    value={statusFilter}
                                    onChange={handleStatusFilterChange}
                                    placeholder={getTranslation('promocodes.status', language)}
                                    margin="mb-0"
                                />
                            </div>
                        </div>
                    </div>
                    {/* Add Button Row */}
                    <div className="flex justify-end">
                    <Button
                        variant="primary"
                        size="md"
                        onClick={onCreatePromocode}
                            className="w-full sm:w-auto sm:min-w-[180px]"
                    >
                        + {getTranslation('promocodes.addPromocode', language)}
                    </Button>
                    </div>
                </div>
            </div>

            {/* Table - Desktop View */}
            <div className="hidden md:block overflow-x-auto overflow-y-visible">
                <div className="relative min-w-full">
                <table className="w-full min-w-[800px]">
                    <thead className="bg-white dark:bg-[#0d1117] border-b dark:border-[#30363d]">
                        <tr>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider min-w-[120px]">
                                {getTranslation('promocodes.promocode', language)}
                            </th>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider min-w-[110px]">
                                {getTranslation('promocodes.startDate', language)}
                            </th>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider min-w-[110px]">
                                {getTranslation('promocodes.endDate', language)}
                            </th>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider min-w-[130px]">
                                {getTranslation('promocodes.type', language)}
                            </th>
                            <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider min-w-[100px]">
                                {getTranslation('promocodes.status', language)}
                            </th>
                            <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider min-w-[80px]">
                                {getTranslation('common.actions', language)}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-[#161b22] divide-y divide-gray-200 dark:divide-[#30363d]">
                        {promocodes.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="px-4 lg:px-6 py-12 text-center text-gray-500 dark:text-[#8b949e] font-medium">
                                    {getTranslation('common.noDataFound', language)}
                                </td>
                            </tr>
                        ) : (
                            promocodes.map((promocode) => (
                                <tr key={promocode.id} className="hover:bg-white dark:hover:bg-[#1c2128]">
                                    <td className="px-4 lg:px-6 py-4">
                                        <div className="text-sm font-medium text-gray-900 dark:text-[#c9d1d9] break-words">
                                            {promocode.code}
                                        </div>
                                    </td>
                                    <td className="px-4 lg:px-6 py-4">
                                        <div className="text-sm text-gray-900 dark:text-[#c9d1d9] font-medium">
                                            {formatDate(promocode.validFrom)}
                                        </div>
                                    </td>
                                    <td className="px-4 lg:px-6 py-4">
                                        <div className="text-sm text-gray-900 dark:text-[#c9d1d9] font-medium">
                                            {formatDate(promocode.validTo)}
                                        </div>
                                    </td>
                                    <td className="px-4 lg:px-6 py-4">
                                        <div className="text-sm text-gray-900 dark:text-[#c9d1d9] font-medium break-words">
                                            {getTypeDisplay(promocode.type)}
                                        </div>
                                    </td>
                                    <td className="px-4 lg:px-6 py-4">
                                        <div className={`text-sm font-medium ${getStatusColor(promocode.isActive)} dark:${promocode.isActive ? 'text-green-400' : 'text-red-400'}`}>
                                            {getStatusDisplay(promocode.isActive)}
                                        </div>
                                    </td>
                                    <td className="px-4 lg:px-6 py-4 text-right">
                                        <div className="flex justify-end">
                                        <Dropdown
                                            isOpen={openDropdown === promocode.id}
                                            onOpenChange={(isOpen) => setOpenDropdown(isOpen ? promocode.id : null)}
                                            position="bottom-right"
                                            usePortal={true}
                                            className="w-48"
                                            trigger={
                                            <button
                                                    type="button"
                                                className="p-2 hover:bg-gray-100 dark:hover:bg-[#1c2128] rounded-md transition-colors"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const newState = openDropdown === promocode.id ? null : promocode.id;
                                                        setOpenDropdown(newState);
                                                    }}
                                            >
                                                <img src={ellipsis} alt="actions" className="w-4 h-4 dark:invert dark:opacity-80" />
                                            </button>
                                            }
                                        >
                                            <DropdownMenuItem
                                                onClick={() => {
                                                    const promocodeId = promocode.id;
                                                    setOpenDropdown(null);
                                                    if (onEditPromocode) {
                                                        onEditPromocode(promocodeId);
                                                    }
                                                }}
                                            >
                                                {getTranslation('common.edit', language)}
                                            </DropdownMenuItem>
                                            <DropdownMenuItem
                                                variant="danger"
                                                onClick={() => {
                                                    setOpenDropdown(null);
                                                    handleDeleteClick(promocode);
                                                }}
                                            >
                                                {getTranslation('common.delete', language)}
                                            </DropdownMenuItem>
                                        </Dropdown>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                </div>
            </div>

            {/* Cards - Mobile View */}
            <div className="md:hidden">
                {promocodes.length === 0 ? (
                    <div className="p-4 sm:p-6 text-center text-gray-500 dark:text-[#8b949e] font-medium">
                        {getTranslation('common.noDataFound', language)}
                    </div>
                ) : (
                    <div className="p-4 space-y-4">
                        {promocodes.map((promocode) => (
                            <div key={promocode.id} className="bg-white dark:bg-[#0d1117] rounded-lg p-4 border border-gray-200 dark:border-[#30363d]">
                                <div className="flex justify-between items-start mb-3">
                                    <div className="flex-1">
                                        <h3 className="text-sm font-medium text-gray-900 mb-1">
                                            {promocode.code}
                                        </h3>
                                        <div className={`text-xs font-medium ${getStatusColor(promocode.isActive)}`}>
                                            {getStatusDisplay(promocode.isActive)}
                                        </div>
                                    </div>
                                    <Dropdown
                                        isOpen={openDropdown === promocode.id}
                                        onOpenChange={(isOpen) => setOpenDropdown(isOpen ? promocode.id : null)}
                                        position="bottom-right"
                                        className="w-48"
                                        trigger={
                                        <button
                                                type="button"
                                                className="p-2 hover:bg-gray-200 dark:hover:bg-[#1c2128] rounded-md transition-colors"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const newState = openDropdown === promocode.id ? null : promocode.id;
                                                    setOpenDropdown(newState);
                                                }}
                                            >
                                                <img src={ellipsis} alt="actions" className="w-4 h-4 dark:invert dark:opacity-80" />
                                        </button>
                                        }
                                    >
                                        <DropdownMenuItem
                                            onClick={() => {
                                                const promocodeId = promocode.id;
                                                setOpenDropdown(null);
                                                if (onEditPromocode) {
                                                    onEditPromocode(promocodeId);
                                                }
                                            }}
                                        >
                                            {getTranslation('common.edit', language)}
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            variant="danger"
                                            onClick={() => {
                                                setOpenDropdown(null);
                                                handleDeleteClick(promocode);
                                            }}
                                        >
                                            {getTranslation('common.delete', language)}
                                        </DropdownMenuItem>
                                    </Dropdown>
                                </div>

                                <div className="grid grid-cols-2 gap-3 text-xs">
                                    <div>
                                        <span className="text-gray-500 font-medium">
                                            {getTranslation('promocodes.startDate', language)}:
                                        </span>
                                        <div className="text-gray-900 mt-1">
                                            {formatDate(promocode.validFrom)}
                                        </div>
                                    </div>
                                    <div>
                                        <span className="text-gray-500 font-medium">
                                            {getTranslation('promocodes.endDate', language)}:
                                        </span>
                                        <div className="text-gray-900 mt-1">
                                            {formatDate(promocode.validTo)}
                                        </div>
                                    </div>
                                    <div className="col-span-2">
                                        <span className="text-gray-500 font-medium">
                                            {getTranslation('promocodes.type', language)}:
                                        </span>
                                        <div className="text-gray-900 mt-1">
                                            {getTypeDisplay(promocode.type)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Pagination */}
            {totalCount > 0 && (
                <div className="flex flex-col sm:flex-row justify-between items-center mt-6 mb-12 px-2">
                    <div className="mb-4 sm:mb-0">
                        <RowsPerPageSelect
                            label={
                                getTranslation('common.rowsPerPage', language) ||
                                'Rows per page'
                            }
                            value={itemsPerPage}
                            onChange={(value) => {
                                setItemsPerPage(value);
                                setCurrentPage(1);
                            }}
                            margin=""
                        />
                    </div>
                    <Pagination
                        totalItems={totalCount}
                        itemsPerPage={itemsPerPage}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                    />
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <ConfirmationModal
                    description={`${getTranslation('common.areYouSure', language)} ${promocodeToDelete?.code}?`}
                    onCancel={() => setShowDeleteModal(false)}
                    onSubmit={handleDeleteConfirm}
                />
            )}
        </div>
    );
};

export default PromocodesTable; 
